import { ArrowRight, Box, ChevronRight, Layers, Plus, Search } from 'lucide-react'
import { type ReactNode, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { locationSeverities, patternKey } from '@/features/schema/findings'
import { filterEntities, filterPatterns, filterRelations } from '@/features/schema/format'
import type { useSchemaManager } from '@/features/schema/hooks/useSchemaManager'
import type { useSchemaValidation } from '@/features/schema/hooks/useSchemaValidation'
import { AskSchemaAgent } from '@/features/schema-agent/SchemaConversationContext'
import { isMobileViewport } from '@/hooks/use-mobile'
import type { ConceptsRegistry, Schema } from '@/services/api/schemas'
import { CatalogueBrowser } from './CatalogueBrowser'
import { ConceptsBrowser } from './ConceptsBrowser'
import { DetailEmptyState } from './DetailEmptyState'
import { EntityDetailCard } from './EntityDetailCard'
import { EntityListItem } from './EntityListItem'
import { FindingsPanel } from './FindingsPanel'
import { ListEmptyState } from './ListEmptyState'
import { PatternDetailCard } from './PatternDetailCard'
import { PatternListItem } from './PatternListItem'
import { RelationDetailCard } from './RelationDetailCard'
import { RelationListItem } from './RelationListItem'

function TabCount({ n }: { n: number }) {
  return (
    <span
      className={`ml-1.5 md:ml-2 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-xs tabular-nums ${
        n > 0 ? 'bg-primary/10 text-primary' : 'bg-muted-foreground/10 text-muted-foreground'
      }`}
    >
      {n}
    </span>
  )
}

/** The scrolling list of types beside (below md: above) the detail card. */
const LIST_PANE =
  'max-h-60 max-md:max-h-[45dvh] @[44rem]:max-h-[600px] space-y-1 overflow-y-auto md:pr-2 scrollbar-dark'

/**
 * On a phone the detail card sits under the list, usually below the fold, so a
 * tap on a row would seem to do nothing. Bring the card into view once the
 * selection has rendered.
 */
function useRevealDetailOnPhone() {
  const detailRef = useRef<HTMLDivElement>(null)
  /** Wrap a row's `onSelect` so selecting also reveals the detail card. */
  const selecting =
    <T,>(select: (item: T) => void) =>
    (item: T) => {
      select(item)
      if (!isMobileViewport()) return
      requestAnimationFrame(() => {
        detailRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
      })
    }
  return { detailRef, selecting }
}

export function SchemaEditorBody({
  sm,
  schema,
  validation,
  editing: { readOnly = false, toolbarEnd } = {},
}: {
  sm: ReturnType<typeof useSchemaManager>
  schema: Schema
  validation: ReturnType<typeof useSchemaValidation>
  /** `readOnly` (viewing a non-latest version) hides every create/edit/delete
   * affordance; `toolbarEnd` trails the add actions (the case editor's schema agent). */
  editing?: { readOnly?: boolean; toolbarEnd?: ReactNode }
}) {
  const { t } = useTranslation()
  const filteredEntities = filterEntities(schema.entity_types, sm.searchQuery)
  const filteredRelations = filterRelations(schema.relationship_types, sm.searchQuery)
  const filteredPatterns = filterPatterns(schema.patterns, sm.searchQuery)

  // Worst severity per offending row, so a list row can flag itself once the
  // schema has been validated.
  const severityByLocation = useMemo(
    () => locationSeverities(validation.findings),
    [validation.findings]
  )
  const validated = validation.findings !== null || validation.serverError !== null
  const { detailRef, selecting } = useRevealDetailOnPhone()
  const selectEntity = selecting(sm.setSelectedEntityForDetail)
  const selectRelation = selecting(sm.setSelectedRelationForDetail)
  const selectPattern = selecting(sm.setSelectedPatternForDetail)

  return (
    <div className="@container space-y-6">
      <Tabs value={sm.activeTab} onValueChange={sm.setActiveTab}>
        {validated && (
          <div className="mb-4">
            <FindingsPanel
              findings={validation.findings}
              findingsBySeverity={validation.findingsBySeverity}
              conceptById={validation.conceptById}
              parseError={null}
              serverError={validation.serverError}
              onDismiss={validation.reset}
            />
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          {/* Below md the strip spans the page and drops its icons, so the
              three tabs fit without scrolling sideways. */}
          <TabsList className="max-md:w-full bg-card border border-border">
            <TabsTrigger value="entities" className="data-[state=active]:bg-muted">
              <Box className="max-md:hidden h-4 w-4 mr-2" />
              {t('schemaAgent.editor.entities')}
              <TabCount n={schema.entity_types.length} />
            </TabsTrigger>
            <TabsTrigger value="relations" className="data-[state=active]:bg-muted">
              <ArrowRight className="max-md:hidden h-4 w-4 mr-2" />
              {t('schemaAgent.editor.relations')}
              <TabCount n={schema.relationship_types.length} />
            </TabsTrigger>
            <TabsTrigger value="patterns" className="data-[state=active]:bg-muted">
              <Layers className="max-md:hidden h-4 w-4 mr-2" />
              {t('schemaAgent.editor.patterns')}
              <TabCount n={schema.patterns.length} />
            </TabsTrigger>
          </TabsList>

          <div className="flex max-md:w-full flex-wrap items-center gap-3">
            <div className="relative max-md:min-w-40 max-md:flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('schemaAgent.editor.search')}
                value={sm.searchQuery}
                onChange={e => sm.setSearchQuery(e.target.value)}
                className="w-full min-w-32 pl-9 bg-muted border-border"
              />
            </div>
            {!readOnly && sm.activeTab === 'entities' && (
              <Button onClick={() => sm.openEntityDialog()}>
                <Plus className="h-4 w-4 mr-1" />
                {t('schemaAgent.editor.addEntity')}
              </Button>
            )}
            {!readOnly && sm.activeTab === 'relations' && (
              <Button onClick={() => sm.openRelationDialog()}>
                <Plus className="h-4 w-4 mr-1" />
                {t('schemaAgent.editor.addRelation')}
              </Button>
            )}
            {!readOnly && sm.activeTab === 'patterns' && (
              <Button onClick={() => sm.openPatternDialog()}>
                <Plus className="h-4 w-4 mr-1" />
                {t('schemaAgent.editor.addPattern')}
              </Button>
            )}
            {toolbarEnd}
          </div>
        </div>

        <TabsContent value="entities" className="mt-0">
          <div className="grid grid-cols-1 @[44rem]:grid-cols-2 gap-4 min-h-[400px]">
            <div className={LIST_PANE}>
              {filteredEntities.map(entity => (
                <EntityListItem
                  key={entity.id}
                  entity={entity}
                  isSelected={sm.selectedEntityForDetail?.id === entity.id}
                  onSelect={selectEntity}
                  severity={severityByLocation.entities[entity.label]}
                />
              ))}
              {filteredEntities.length === 0 && (
                <ListEmptyState icon={Box} message={t('schemaAgent.editor.noEntities')} />
              )}
            </div>

            <div ref={detailRef} className="flex max-md:scroll-my-3 flex-col">
              {sm.selectedEntityForDetail && (
                <AskSchemaAgent label={sm.selectedEntityForDetail.label} />
              )}
              {sm.selectedEntityForDetail ? (
                <EntityDetailCard
                  entity={sm.selectedEntityForDetail}
                  onEdit={sm.openEntityDialog}
                  onDelete={sm.deleteEntity}
                  readOnly={readOnly}
                />
              ) : (
                <DetailEmptyState icon={Box} message={t('schemaAgent.editor.selectEntity')} />
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="relations" className="mt-0">
          <div className="grid grid-cols-1 @[44rem]:grid-cols-2 gap-4 min-h-[400px]">
            <div className={LIST_PANE}>
              {filteredRelations.map(relation => (
                <RelationListItem
                  key={relation.id}
                  relation={relation}
                  isSelected={sm.selectedRelationForDetail?.id === relation.id}
                  onSelect={selectRelation}
                  severity={severityByLocation.relations[relation.label]}
                />
              ))}
              {filteredRelations.length === 0 && (
                <ListEmptyState icon={ArrowRight} message={t('schemaAgent.editor.noRelations')} />
              )}
            </div>

            <div ref={detailRef} className="flex max-md:scroll-my-3 flex-col">
              {sm.selectedRelationForDetail && (
                <AskSchemaAgent label={sm.selectedRelationForDetail.label} />
              )}
              {sm.selectedRelationForDetail ? (
                <RelationDetailCard
                  relation={sm.selectedRelationForDetail}
                  onEdit={sm.openRelationDialog}
                  onDelete={sm.deleteRelation}
                  readOnly={readOnly}
                />
              ) : (
                <DetailEmptyState
                  icon={ArrowRight}
                  message={t('schemaAgent.editor.selectRelation')}
                />
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="patterns" className="mt-0">
          <div className="grid grid-cols-1 @[44rem]:grid-cols-2 gap-4 min-h-[400px]">
            <div className={LIST_PANE}>
              {filteredPatterns.map(pattern => (
                <PatternListItem
                  key={pattern.id}
                  pattern={pattern}
                  isSelected={sm.selectedPatternForDetail?.id === pattern.id}
                  onSelect={selectPattern}
                  severity={severityByLocation.patterns[patternKey(pattern)]}
                />
              ))}
              {filteredPatterns.length === 0 && (
                <ListEmptyState icon={Layers} message={t('schemaAgent.editor.noPatterns')} />
              )}
            </div>

            <div ref={detailRef} className="flex max-md:scroll-my-3 flex-col">
              {sm.selectedPatternForDetail && (
                <AskSchemaAgent
                  label={[
                    sm.selectedPatternForDetail.source_type,
                    sm.selectedPatternForDetail.relationship_type,
                    sm.selectedPatternForDetail.target_type,
                  ].join(' → ')}
                />
              )}
              {sm.selectedPatternForDetail ? (
                <PatternDetailCard
                  pattern={sm.selectedPatternForDetail}
                  onEdit={sm.openPatternDialog}
                  onDelete={sm.deletePattern}
                  readOnly={readOnly}
                />
              ) : (
                <DetailEmptyState icon={Layers} message={t('schemaAgent.editor.selectPattern')} />
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
      {validation.registry && <ConceptRegistrySection registry={validation.registry} />}
    </div>
  )
}

// ==================== Concept registry (governing rules) ====================

/**
 * The concept-registry catalog on the schema detail page (SC-005): the same
 * `ConceptsBrowser` the playground uses. Expanded by default so the governing
 * rules are visible while editing; collapsible to reclaim space.
 */
function ConceptRegistrySection({ registry }: { registry: ConceptsRegistry }) {
  const { t } = useTranslation()
  const count = Object.keys(registry.concepts).length
  return (
    <Collapsible>
      <Card>
        <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-6 py-4 text-left">
          <div>
            <CardTitle className="text-base">{t('schemaAgent.catalogue.reference')}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {t('schemaAgent.editor.governingRules', { count })}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 transition-transform group-data-[state=open]:rotate-90" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pb-6">
            <Tabs defaultValue="concepts">
              <TabsList>
                <TabsTrigger value="concepts">{t('schemaAgent.catalogue.concepts')}</TabsTrigger>
                <TabsTrigger value="catalogue">{t('schemaAgent.catalogue.title')}</TabsTrigger>
              </TabsList>
              <TabsContent value="concepts">
                <ConceptsBrowser registry={registry} />
              </TabsContent>
              <TabsContent value="catalogue">
                <CatalogueBrowser />
              </TabsContent>
            </Tabs>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
